import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import LaddaButton from "../../components/buttons/laddaButton";
import HighchartsComponent from "../../components/highchartsComponent";
import SimpleSelect from "../../components/selects/simpleSelect";
import SimpleTable from "../../components/tables/simpleTable";
import { useForm } from "react-hook-form";
import { versusLine } from "../../components/highchartsComponent/charts/lineVersus";
import BiomedicalCard from "../../components/cards/biomedicalCard";
import { Grid, useMediaQuery } from "@mui/material";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import "./styles.css";
import { icons } from "../../constants";
import {
  callApiAsync,
  getEngineeringActivityInfo,
  getSelectsServiceOrder,
  getServiceOrdersAnualChartData,
  getServiceOrderDurationByOrder,
  getServiceOrderDowntimeIndicator,
  getOverThresholdCount,
  getServiceOrderDateRange
} from "../../services/apiService";
import OpenClosedindicatorTable from "./indicators/OpenClosedindicatorTable";
import { months } from "../../constants/selectData";
import { resetServiceOrderId } from "../../redux/slices/serviceOrderSlice";
import { useDispatch } from "react-redux";
import IndicatorAnalisisModal from "./modals/indicatorAnalisisModal";
import { orderSelectActivity } from "../../utils/orderActivity";
import MultiSelect from "../../components/selects/multiSelect";
import { CustomTabPanel } from "../../components/tabComponent";
import { lineChartOptions } from "../../components/highchartsComponent/charts/lines";
import TimeAnalisisModal from "./modals/timeAnalisisModal";
import ModalComponent from "../../components/modals";
import { engineerReportTypeService } from "../../constants/enums";
import { StyledTooltip } from "../../components/toolltipComponent";
import { useLoadingState } from "../../components/loadingFeedback";

const validationSchema = yup.object().shape({});

const ManagementDashboardIndicator = () => {
  const [annualRequestLoading, setLoadingAnnual] = useLoadingState();
  const [timeRequestLoading, setLoadingTime] = useLoadingState();
  const [infoRequestLoading, setLoadingInfo] = useLoadingState();
  const [loadingFilters, setLoadingFilters] = useLoadingState(true);
  const loadingAnnual = annualRequestLoading || loadingFilters;
  const loadingTime = timeRequestLoading || loadingFilters;
  const loadingInfo = infoRequestLoading || loadingFilters;
  const dispatch = useDispatch();
  const [openIndicator, setOpenIndicator] = useState(false);
  const [openTimeIndicator, setOpenTimeIndicator] = useState(false);
  const [activityData, setActivityData] = useState([]);
  const [engineerList, setEngineerList] = useState([]);
  const [institutionListSelect, setInstitutionListSelect] = useState([]);
  const [filterList, setFilterList] = useState([]);
  const [activityInfo, setActivityInfo] = useState([]);
  const [activityInfoChart, setActivityInfoChart] = useState([]);
  const [serviceOrderAnualTable, setServiceOrderAnualTable] = useState([]);
  const [totalClosed, setTotalClosed] = useState("-");
  const [totalOpen, setTotalOpen] = useState("-");
  const [totalPercentage, setTotalPercentage] = useState(null);
  const [timeChardData, setTimeChardData] = useState([]);
  const [timeChardCategories, setTimeChardCategories] = useState([]);
  const [timeData, setTimeData] = useState({});
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [filtersForModal, setFiltersForModal] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [created, setCreated] = useState("");
  const [serviceOrderDurationData, setServiceOrderDurationData] = useState({});
  const [serviceOrderDurationMeta, setServiceOrderDurationMeta] = useState({});
  const [alternateView, setAlternateView] = useState(false);
  const [altChartData, setAltChartData] = useState([]);
  const [altChartCategories, setAltChartCategories] = useState([]);
  const [altDurationData, setAltDurationData] = useState({});
  const [altKpis, setAltKpis] = useState({});
  const [thresholdNotif, setThresholdNotif] = useState(null);
  const [serviceOrderYearOptions, setServiceOrderYearOptions] = useState([]);

  const [filtersInitialized, setFiltersInitialized] = useState(false);

  const [selectedFilters, setSelectedFilters] = useState({
    activity: null,
    engineer: null,
    year: null,
    month: null,
    institution: null
  });

  const {
    control,
    setValue,
    getValues,
    watch,
    reset,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: "all"
  });

  dispatch(resetServiceOrderId());

  const mockDurationData = {
    averageDaysClosed: "7 días",
    totalClosedInMonth: 5
  };

  const isSmallScreen = useMediaQuery("(max-width:1000px)");
  const isSmallXLScreen = useMediaQuery("(max-width:700px)");

  const watchActivity = watch("activity");
  const watchEnginner = watch("engineer");
  const watchYear = watch("year");
  const watchMonth = watch("month");
  const watchInstitution = watch("institution");

  let location = useLocation();

  const handleBlackbellClick = () => {
    setAlternateView((prev) => !prev);
  };

  const handleChangeTable = () => {
    const formData = getValues();
  };

  const selectsServiceOrder = async () => {
    await callApiAsync(
      () => getSelectsServiceOrder(),
      (data) => {
        setFilterList(data);
        setActivityData(orderSelectActivity(data.activityList));
        setEngineerList(data.enginnerList);
        setInstitutionListSelect(data.institutionList);
      },
      setLoadingFilters
    );
  };

  const getServiceOrderYearsData = async () => {
    await callApiAsync(
      () => getServiceOrderDateRange(),
      (data) => {
        const currentYear = new Date().getFullYear();
        const years =
          Array.isArray(data?.years) && data.years.length
            ? data.years
            : [{ name: String(currentYear), value: currentYear }];

        setServiceOrderYearOptions(years);
      },
      setLoadingFilters
    );
  };

  const getServiceOrdersAnualChart = async () => {
    await callApiAsync(
      () =>
        getServiceOrdersAnualChartData(
          "detailed",
          watchYear,
          watchMonth,
          watchActivity,
          watchInstitution ?? null,
          watchEnginner === "" ? null : watchEnginner
        ),
      (data) => {
        setServiceOrderAnualTable(data.seriesData);
        setTotalClosed(data.raw.monthDetail.closed);
        setTotalOpen(data.raw.monthDetail.open);
        setCreated(data.raw.monthDetail.created);

        setTotalPercentage(data.compliance.percentage);
      },
      setLoadingAnnual
    );
  };

  const handleClearFilters = () => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    let defaultActivity = null;
    defaultActivity = activityData[0].value;

    reset({
      activity: defaultActivity,
      year: currentYear,
      month: currentMonth,
      engineer: null,
      institution: null
    });

    setSelectedFilters({
      activity: defaultActivity,
      engineer: null,
      year: currentYear,
      month: currentMonth,
      institution: null
    });

    setFiltersInitialized(false);
    handleFilter();
  };

  const handleFilter = () => {
    const filters = getValues();

    setSelectedFilters({
      activity: filters.activity,
      engineer: filters.engineer,
      year: filters.year,
      month: filters.month,
      institution: filters.institution
    });

    setFiltersApplied(true);

    if (filters.activity === 1) {
      getNotificationCount();
    }

    getEngineeringActivityInfoData();
    getEngineeringActivityInfoChartData();
    getServiceOrdersAnualChart();
    getTimeAnalysisChartData();
    getTimeAnalysisCardsData();
  };

  const categoriesData = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre"
  ];

  const getEngineeringActivityInfoData = async () => {
    if (!watchActivity) return;
    await callApiAsync(
      () => getEngineeringActivityInfo(watchActivity, 1),
      (data) => {
        setActivityInfo([data]);
      },
      setLoadingInfo
    );
  };

  const getEngineeringActivityInfoChartData = async () => {
    if (!watchActivity) return;
    await callApiAsync(
      () => getEngineeringActivityInfo(watchActivity, 2),
      (data) => {
        setActivityInfoChart(Array.isArray(data) ? data : data ? [data] : []);
      },
      setLoadingInfo
    );
  };

  const columns = [
    {
      field: "objectiveIndicator",
      header: "Objetivo del Indicador",
      columnWidth: "160px",
      columnType: "tooltip"
    },
    {
      field: "responsible",
      header: "Responsable",
      columnWidth: "160px"
    },
    {
      field: "indicator",
      header: "Indicador",
      columnWidth: "160px"
    },
    {
      field: "indexData",
      header: "Indice",
      columnWidth: "160px",
      columnType: "tooltip"
    },
    {
      field: "goal",
      header: "Meta",
      columnWidth: "160px"
    },
    {
      field: "rangeData",
      header: "Rango",
      columnWidth: "160px"
    }
  ];

  const ToolTipDescription = (text) => {
    return (
      <div>
        <p className="tooltip-text">{text}</p>
      </div>
    );
  };

  const handleCloseIndicator = () => {
    setOpenIndicator(false);
  };

  const handleOpenIndicator = () => {
    setOpenIndicator(true);
  };

  const handleOpenTimeIndicator = () => setOpenTimeIndicator(true);
  const handleCloseTimeIndicator = () => setOpenTimeIndicator(false);
  const handleOpenOrdersModal = () => {
    const currentFilters = getValues();
    setFiltersForModal(currentFilters);
    setModalOpen(true);
  };
  const handleCloseOrdersModal = () => setModalOpen(false);

  const selectedActivityName =
    activityData.find((item) => item.value === watchActivity)?.name ?? "";

  const transformedSeries = timeChardData?.map((serie) => ({
    ...serie,
    data: serie.data.map((y, i) => ({
      y,
      datalabel: serie.datalabel[i]
    }))
  }));

  // funcion para la grafica
  const getTimeAnalysisChartData = async () => {
    if (
      alternateView &&
      watchActivity === engineerReportTypeService.CorrectiveMaintenance
    ) {
      await callApiAsync(
        () =>
          getServiceOrderDowntimeIndicator(
            watchYear,
            watchMonth,
            watchActivity
          ),
        (data) => {
          setAltChartData(data.chartData);
          setAltChartCategories(data.categories);
          setAltDurationData(data.chartCount);
          setAltKpis(data.kpis);
        },
        setLoadingTime
      );
    } else {
      await callApiAsync(
        () =>
          getServiceOrderDurationByOrder(
            "detailed",
            watchYear,
            watchMonth,
            watchActivity,
            watchInstitution,
            watchEnginner === "" ? null : watchEnginner
          ),
        (data) => {
          setTimeChardData(data.graph);
          setTimeChardCategories(data.categories);
        },
        setLoadingTime
      );
    }
  };

  const getNotificationCount = async () => {
    await callApiAsync(
      () => getOverThresholdCount(watchYear, watchMonth),
      (data) => {
        setThresholdNotif(data);
      }
    );
  };

  // funcion para la tarjeta
  const getTimeAnalysisCardsData = async () => {
    if (alternateView) {
    } else {
      await callApiAsync(
        () =>
          getServiceOrderDurationByOrder(
            "detailed",
            watchYear,
            watchMonth,
            watchActivity,
            watchInstitution,
            watchEnginner === "" ? null : watchEnginner
          ),
        (data) => {
          setTimeData(data.averageDuration);
          setServiceOrderDurationData(data.totals);
          setServiceOrderDurationMeta(data.meta);
        },
        setLoadingTime
      );
    }
  };

  useEffect(() => {
    getTimeAnalysisChartData();
    getTimeAnalysisCardsData();
  }, [
    alternateView,
    watchYear,
    watchMonth,
    watchActivity,
    watchInstitution,
    watchEnginner
  ]);

  useEffect(() => {
    selectsServiceOrder();
    getServiceOrderYearsData();
  }, []);

  useEffect(() => {
    if (
      activityData.length > 0 &&
      serviceOrderYearOptions.length > 0 &&
      !filtersInitialized
    ) {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;

      setValue("activity", activityData[0].value);
      setValue("year", currentYear);
      setValue("month", currentMonth);

      setFiltersInitialized(true);
    }
  }, [activityData, serviceOrderYearOptions, filtersInitialized, setValue]);

  useEffect(() => {
    if (filtersInitialized) {
      handleFilter();
    }
  }, [filtersInitialized]);

  const activityTooltips = {
    "Ajuste de consumibles": (
      <>
        <p className="tooltip_info_title">Ajuste de consumibles</p>
        <p className="tooltip_text">
          Configuración y programación manual de jeringas de marcas comerciales
          en las bombas de infusión ajustando las especificaciones necesarias
          para su correcta integración y funcionamiento.
        </p>
      </>
    ),
    Alistamiento: (
      <>
        <p className="tooltip_info_title">Alistamiento</p>
        <p className="tooltip_text">
          Conjunto de actividades orientadas a la organización documental,
          limpieza y verificación de los dispositivos médicos previo a su
          entrega a una institución, garantizando que se encuentren en
          condiciones adecuadas para su uso.
        </p>
      </>
    ),
    "Asistencia técnica": (
      <>
        <p className="tooltip_info_title">Asistencia técnica</p>
        <p className="tooltip_text">
          Actividades técnicas relacionadas con el soporte telefónico, la
          verificación de tecnología retirada de las instituciones y las visitas
          en las que no se requiere una intervención directa sobre los equipos
          biomédicos, limitándose al reemplazo de accesorios como cables de
          poder, soportes para atril, sensores acelerómetros, entre otros.
        </p>
      </>
    ),
    "Actualización de software": (
      <>
        <p className="tooltip_info_title">Actualización de software</p>
        <p className="tooltip_text">
          Es el proceso mediante el cual se incorpora una nueva versión del
          sistema operativo, firmware o software de control de un equipo
          biomédico, con el fin de mejorar su rendimiento, corregir errores,
          aumentar la seguridad o incorporar nuevas funcionalidades.
        </p>
      </>
    ),
    Instalación: (
      <>
        <p className="tooltip_info_title">Instalación</p>
        <p className="tooltip_text">
          Proceso mediante el cual se ubica y ensambla un equipo biomédico en el
          lugar destinado para su funcionamiento, asegurando la integración de
          su hardware y software, así como la correcta conexión de sus sistemas
          (eléctrico, mecánico, neumático, electrónico, entre otros), con el fin
          de dejarlo en condiciones óptimas de operación.
        </p>
      </>
    ),
    "Mantenimiento correctivo": (
      <>
        <p className="tooltip_info_title">Mantenimiento correctivo</p>
        <p className="tooltip_text">
          Intervención realizada cuando un equipo biomédico presenta una falla,
          error o defecto, con el fin de restablecer sus condiciones normales de
          funcionamiento.
        </p>
      </>
    ),
    "Visita Predictiva": (
      <>
        <p className="tooltip_info_title">Visita Predictiva</p>
        <p className="tooltip_text">
          Actividades orientadas a evaluar el estado y el desempeño de los
          equipos biomédicos, anticipando posibles fallas mediante pruebas de
          funcionamiento y retroalimentación del área de ingeniería biomédica.
        </p>
      </>
    ),
    "Mantenimiento preventivo": (
      <>
        <p className="tooltip_info_title">Mantenimiento preventivo</p>
        <p className="tooltip_text">
          Conjunto de acciones planificadas que se ejecutan de forma periódica
          para minimizar el riesgo de fallas y garantizar la continuidad
          operativa de los equipos biomédicos.
        </p>
      </>
    ),
    Preinstalación: (
      <>
        <p className="tooltip_info_title">Preinstalación</p>
        <p className="tooltip_text">
          Es el conjunto de actividades técnicas que se realizan antes de la
          instalación de un equipo biomédico, con el objetivo de verificar que
          el área designada cumple con los requisitos físicos, eléctricos,
          ambientales y de conectividad establecidos por el fabricante. Esta
          etapa asegura que el entorno sea adecuado para recibir y operar el
          equipo de forma segura y eficiente.
        </p>
      </>
    )
  };

  return (
    <CustomTabPanel value="1">
      <div className="form-container-open-closed">
        <Grid container spacing={1}>
          <Grid item xs={12} sm={3} md={3}>
            <p className="info-label">Actividad:</p>
            <SimpleSelect
              name="activity"
              control={control}
              label=""
              options={activityData}
              onChange={() => handleChangeTable()}
              margin={"0"}
              error={errors.activity}
            />
          </Grid>
          <Grid item xs={12} sm={1.5} md={1.5}>
            <p className="info-label">Año:</p>
            <SimpleSelect
              name="year"
              control={control}
              label=""
              options={serviceOrderYearOptions}
              onChange={() => handleChangeTable()}
              margin={"0"}
              error={errors.year}
            />
          </Grid>
          <Grid item xs={12} sm={1.5} md={1.5}>
            <p className="info-label">Mes:</p>
            <SimpleSelect
              name="month"
              control={control}
              label=""
              options={months}
              onChange={() => handleChangeTable()}
              margin={"0"}
              error={errors.month}
            />
          </Grid>
          <Grid item xs={12} sm={3} md={3}>
            <p className="info-label">Ingeniero/a:</p>
            <SimpleSelect
              name="engineer"
              control={control}
              label=""
              options={engineerList}
              onChange={() => handleChangeTable()}
              margin={"0"}
              error={errors.engineer}
            />
          </Grid>
          <Grid item xs={12} sm={3} md={3}>
            <p className="info-label">Institución:</p>
            <div className="biomedical-detail-institution-filter">
              <MultiSelect
                name="institution"
                control={control}
                label=""
                options={institutionListSelect}
                onChange={() => handleChangeTable()}
                margin={"0"}
                error={errors.institution}
                isMultiple={false}
                searchBar={true}
              />
            </div>
          </Grid>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "0"
                }}
              >
                <LaddaButton
                  variant="contained"
                  onClick={() => handleFilter()}
                  margin={"10px"}
                >
                  <img
                    className="btn-icon"
                    src={icons.filterWhite}
                    alt="icon"
                  />
                </LaddaButton>
                <LaddaButton
                  variant="contained"
                  onClick={handleClearFilters}
                  margin={"10px 0 0 10px"}
                >
                  <img
                    className="btn-icon"
                    src={icons.clearFilterWhite}
                    alt="icon"
                  />
                </LaddaButton>
              </div>
            </Grid>
          </Grid>
        </Grid>
      </div>
      <div className="table-container">
        <br className="br-white-space" />
        
        <div className="HContainer j-space-between a-center">
          <h1
            className="title-bio-details-chard"
            style={{ display: "flex", alignItems: "center" }}
          >
            {`Cumplimiento de Ordenes de Servicio de ${
              activityData.find(
                (item) => item.value === selectedFilters.activity
              )?.name ?? ""
            } ${
              engineerList.find(
                (item) => item.value === selectedFilters.engineer
              )?.name
                ? `de ${engineerList
                    .find((item) => item.value === selectedFilters.engineer)
                    .name.toUpperCase()}`
                : ""
            }`}
            <StyledTooltip
              title={
                activityTooltips[
                  activityData.find(
                    (item) => item.value === selectedFilters.activity
                  )?.name
                ] ?? <p>No hay información disponible</p>
              }
              followCursor
            >
              <img
                className="btn-add-icon"
                src={icons.infoExclamation}
                alt="icon"
                style={{
                  marginLeft: "8px",
                  width: "30px",
                  height: "30px",
                  cursor: "pointer"
                }}
              />
            </StyledTooltip>
          </h1>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <LaddaButton
              variant="contained"
              onClick={handleOpenOrdersModal}
              margin={"0"}
            >
              <img
                className="btn-icon"
                src={icons.clearFilterWhite}
                alt="icon"
              />
              <span style={{ marginLeft: "5px" }}>Ver órdenes</span>
            </LaddaButton>

            <LaddaButton
              variant="contained"
              onClick={handleOpenIndicator}
              margin={"0"}
              backgroundColor={"#F4D658"}
            >
              <img
                className="btn-icon"
                src={icons.analisis_glass_black}
                alt="icon"
              />
              <span className="text-black" style={{ marginLeft: "5px" }}>
                Análisis
              </span>
            </LaddaButton>
          </div>
        </div>

        <ModalComponent open={modalOpen} handleClose={handleCloseOrdersModal}
          title="Órdenes de servicio" width="1050px" maxWidth="calc(100vw - 32px)">
            <OpenClosedindicatorTable
              control={control}
              getValues={getValues}
              reset={reset}
              filterList={filtersForModal}
            />
        </ModalComponent>

        <br />
        {(loadingInfo || (activityInfo && activityInfo.length > 0)) && (
          <SimpleTable
            loading={loadingInfo}
            data={activityInfo}
            columns={columns}
            tooltipFragment={ToolTipDescription}
          />
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: isSmallScreen ? "center" : "flex-start",
            gap: "20px",
            marginTop: "10px",
            flexDirection: isSmallScreen ? "column" : "row"
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 200px)",
              gridGap: "15px"
            }}
          >
            <BiomedicalCard
              loading={loadingAnnual}
              largeText={` ${created ?? created + " creadas"} `}
              smallText="Órdenes Creadas"
            />
            <BiomedicalCard
              loading={loadingAnnual}
              largeText={` ${totalClosed ?? totalClosed + " cerradas"} `}
              smallText="Órdenes Cerradas"
              bgColor="#DDF3FF"
            />
            <BiomedicalCard
              loading={loadingAnnual}
              largeText={totalOpen ?? totalOpen}
              smallText="Órdenes Abiertas"
              bgColor="#DDF3FF"
            />
            <BiomedicalCard
              loading={loadingAnnual}
              largeText={
                totalPercentage !== null && totalPercentage !== undefined
                  ? `${totalPercentage} `
                  : "—"
              }
              smallText="Cumplimiento"
            />
          </div>
          <div style={{ flex: 1, minWidth: "300px" }}>
            <HighchartsComponent
              loading={loadingAnnual}
              options={versusLine(
                "",
                "",
                "Cantidad de Ordenes de Servicio",
                serviceOrderAnualTable,
                categoriesData,
                "Meses"
              )}
            />
          </div>
        </div>
      </div>
      <div className="table-container">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "15px"
          }}
        >
          <h1
            className="title-bio-details-chard"
            style={{ fontSize: isSmallXLScreen ?? "14px" }}
          >
            {alternateView
              ? "Indicador de tiempo de parada"
              : `Control de los tiempos de ejecución de las Ordenes de Servicio de ${
                  activityData.find(
                    (item) => item.value === selectedFilters.activity
                  )?.name ?? ""
                }`}
          </h1>
          <div style={{ display: "flex", gap: "10px" }}>
            {watchActivity ===
            engineerReportTypeService.CorrectiveMaintenance ? (
              <LaddaButton
                variant="contained"
                onClick={() => {
                  if (mockDurationData.totalClosedInMonth > 0) {
                    handleBlackbellClick();
                  }
                }}
                margin={"0"}
                backgroundColor={"#F4D658"}
                height={"42px"}
                disabled={mockDurationData.totalClosedInMonth <= 0}
              >
                {mockDurationData.totalClosedInMonth > 0 && (
                  <div className="notification-circle">
                    {thresholdNotif?.count ?? 0}
                  </div>
                )}
                <img
                  className="btn-icon-analisis"
                  src={icons.blackBell}
                  alt="icon"
                  style={{ width: "25px", height: "25px" }}
                />
              </LaddaButton>
            ) : (
              <></>
            )}

            <LaddaButton
              variant="contained"
              onClick={handleOpenTimeIndicator}
              margin={"0"}
              backgroundColor={"#F4D658"}
            >
              <img
                className="btn-icon"
                src={icons.analisis_glass_black}
                alt="icon"
              />
              <span className="text-black" style={{ marginLeft: "5px" }}>
                Análisis
              </span>
            </LaddaButton>
          </div>
        </div>
        {/* Tabla condicional */}
        {alternateView ? (
          <SimpleTable
            loading={loadingTime}
            data={[
              {
                objectiveIndicator:
                  "Dar seguimiento a las órdenes de servicio de mantenimiento correctivo  que exceden los 5 días hábiles establecidos para la reparación de la tecnología biomédica con el propósito de garantizar su disponibilidad permanente en las instituciones.",
                responsible: "Coordinador de soporte técnico",
                indicator: "Tiempos de parada",
                indexData:
                  "(# de equipos biomédicos con tiempo de parada mayor a 5 días  /# de equipos biomédicos ingresados para mantenimiento.",
                goal: "5%",
                rangeData: "Mensual"
              }
            ]}
            columns={columns}
            tooltipFragment={ToolTipDescription}
          />
        ) : (
          <SimpleTable
            loading={loadingInfo}
            data={activityInfoChart}
            columns={columns}
            tooltipFragment={ToolTipDescription}
          />
        )}
        <br />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: isSmallScreen ? "center" : "flex-start",
            gap: "20px",
            marginTop: "10px",
            flexDirection: isSmallScreen ? "column" : "row"
          }}
        >
          <div style={{ flex: 1, minWidth: "300px" }}>
            <HighchartsComponent
              loading={loadingTime}
              options={lineChartOptions(
                "",
                "",
                "Cantidad de Días",
                alternateView ? altChartData : transformedSeries,
                alternateView ? altChartCategories : timeChardCategories,
                "Cantidad de Ordenes de Servicio"
              )}
            />
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "15px",
              alignItems: "center",
              minWidth: "250px"
            }}
          >
            {alternateView && (
              <BiomedicalCard
                loading={loadingTime}
                largeText={
                  serviceOrderDurationData
                    ? serviceOrderDurationData.totalClosedInMonth
                    : "-" + " días"
                }
                smallText="Total de ordenes en mantenimiento correctivo "
                width={"360px"}
              />
            )}
            <BiomedicalCard
              loading={loadingTime}
              largeText={
                alternateView
                  ? altKpis.chartCount
                  : serviceOrderDurationData
                  ? serviceOrderDurationData.totalClosedInMonth
                  : "-"
              }
              smallText={`${
                alternateView
                  ? "Total de ordenes en parada"
                  : "Cantidad de ordenes"
              }`}
              width={"360px"}
              bgColor="#DDF3FF"
            />

            <BiomedicalCard
              loading={loadingTime}
              largeText={
                alternateView
                  ? altKpis.chartAverageDays + " días"
                  : serviceOrderDurationData
                  ? serviceOrderDurationData.averageDaysClosed
                  : "-" + " días"
              }
              smallText="Promedio por orden de servicio"
              width={"360px"}
            />

            <BiomedicalCard
              loading={loadingTime}
              smallText="Cumplio la meta"
              midText={
                alternateView
                  ? altKpis?.meets === true
                    ? "SI"
                    : "NO"
                  : serviceOrderDurationMeta.meetsGoal === true
                  ? "SI"
                  : "NO"
              }
              width={"360px"}
              bgColor="#DDF3FF"
            />
          </div>
        </div>
      </div>
      <TimeAnalisisModal
        open={openTimeIndicator}
        handleClose={handleCloseTimeIndicator}
        alternateView={alternateView}
        activityId={watchActivity}
        activityName={selectedActivityName}
      />
      <IndicatorAnalisisModal
        open={openIndicator}
        handleClose={handleCloseIndicator}
        data={watchActivity || null}
      />
    </CustomTabPanel>
  );
};

export default ManagementDashboardIndicator;
